import React, { useEffect, useRef, useState } from 'react';
import { TextField } from '@mui/material';
import { FieldTitle, InputHelperText, maxValue, minValue, required, useInput } from 'react-admin';

// `discount_value` é FRAÇÃO (0–1, onde 1 = 100%) no banco e na API externa,
// mas o campo mostra percentual. Toda a conversão mora aqui.
const PERCENTAGE_SCALE = 100;

// 2 casas no percentual = 4 na fração (12,33% → 0.1233). O FLOAT8 da coluna e o
// Joi do backend (min .01 / max 1, sem `precision`) já aceitavam isso.
const PERCENTAGE_DECIMALS = 2;
const FRACTION_DECIMALS = 4;

const isEmpty = (value) => value === '' || value === null || value === undefined;

// Sai com vírgula: o painel fala pt-BR e espanhol, e as duas usam vírgula decimal.
const formatPercentage = (value) => {
  if (isEmpty(value) || Number.isNaN(Number(value))) return '';
  // O toFixed não é cosmético: 0.07 * 100 dá 7.000000000000001 em float64, e sem
  // ele o campo exibiria isso ao abrir um produto já cadastrado com 7%.
  const percentage = +(Number(value) * PERCENTAGE_SCALE).toFixed(PERCENTAGE_DECIMALS);
  return String(percentage).replace('.', ',');
};

const parsePercentage = (text) => {
  if (isEmpty(text)) return null;
  const normalized = String(text).trim().replace(',', '.');
  if (normalized === '' || normalized === '.' || !/^\d*\.?\d*$/.test(normalized)) return null;
  return +(Number(normalized) / PERCENTAGE_SCALE).toFixed(FRACTION_DECIMALS);
};

// Campo de TEXTO, não `type="number"`: num input numérico quem decide se a vírgula
// vale é o locale do NAVEGADOR — num Chrome en-US ela é descartada antes do JS ver e
// o onChange chega vazio. Aqui os dois separadores entram, em qualquer navegador.
//
// O texto digitado fica em estado local e só ressincroniza com o form fora do foco.
// Sem isso o valor exibido seria `format(valor)` a cada tecla e a vírgula sumiria no
// meio da digitação: "12," vira 12, que formata de volta para "12".
const DiscountPercentageInput = ({
  source = 'discountValue',
  label = 'Valor do Desconto(%)',
  helperText = undefined,
  validate = [
    required(),
    minValue(0.01, 'Deve ser no mínimo 1%'),
    maxValue(1, 'Deve ser no máximo 100%'),
  ],
  sx = { width: 150 },
  inputProps = {},
  ...props
}) => {
  const { field, fieldState: { error, invalid }, id, isRequired } = useInput({
    source,
    validate,
    defaultValue: 1,
  });

  const [text, setText] = useState(() => formatPercentage(field.value));
  const hasFocus = useRef(false);

  useEffect(() => {
    if (hasFocus.current) return;
    const formatted = formatPercentage(field.value);
    setText((current) => (current === formatted ? current : formatted));
  }, [field.value]);

  return (
    <TextField
      id={id}
      name={field.name}
      inputRef={field.ref}
      value={text}
      onChange={(event) => {
        setText(event.target.value);
        field.onChange(parsePercentage(event.target.value));
      }}
      onFocus={() => { hasFocus.current = true; }}
      onBlur={(event) => {
        hasFocus.current = false;
        // Reescreve o campo com o que foi de fato guardado — é o que impede uma
        // digitação mal interpretada de passar despercebida.
        setText(formatPercentage(field.value));
        field.onBlur(event);
      }}
      label={<FieldTitle label={label} source={source} isRequired={isRequired} />}
      error={invalid}
      helperText={
        helperText !== false || invalid
          ? <InputHelperText error={error?.message} helperText={helperText} />
          : null
      }
      size="small"
      inputProps={{ inputMode: 'decimal', ...inputProps }}
      sx={sx}
      {...props}
    />
  );
};

export default DiscountPercentageInput;
